/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/**
 * useTabState - Core Tab State Management
 *
 * This is a foundational hook that manages the core tab state for the editor.
 * It operates at the TabManager (context provider) level and provides:
 * - React state for tabs array and active tab
 * - Ref-based storage for immediate access to current state
 * - Content storage maps for tab persistence
 * - Helper functions for state synchronization
 *
 * Architecture Position:
 * TabManager
 * ├── useTabState() ◄── Creates core state (this hook)
 * ├── useBlockCache() ◄── Creates cache state
 * └── Context Provider ◄── Provides both to children
 *
 * Note: This hook doesn't use context since it's responsible for creating
 * the state that gets passed INTO the context.
 */

import { useState, useRef } from 'react';

import { Tab, createNewTab } from '../helpers/codeEditorHelpers';
import { MetadataManager } from '../managers/MetadataManager';

/**
 * Initialize and manage tab state for the TabManager
 *
 * @param initialCode - Starting code content for the main tab
 * @returns Object containing state, refs, and helper functions
 */
export const useTabState = (initialCode: string) => {
    // === CONTENT STORAGE REFS ===
    // These provide immediate access to content without triggering re-renders

    /** Map of tab ID to current code content */
    const tabContentRef = useRef<Map<string, string>>(new Map());

    /** Map of tab ID to original content (for modification tracking) */
    const tabOriginalContentRef = useRef<Map<string, string>>(new Map());

    /** Current active tab ID (for immediate access) */
    const activeTabIdRef = useRef<string>('main');

    /** All tabs array (for immediate access) */
    const tabsRef = useRef<Tab[]>([]);

    // === REACT STATE ===
    // These trigger re-renders when changed

    /** Current active tab ID (React state) */
    const [activeTabId, setActiveTabId] = useState<string>('main');

    /** All tabs array with initialization */
    const [tabs, setTabs] = useState<Tab[]>(() => {
        // Create the main tab with initial content
        const mainTab = createNewTab(
            'main',
            'Main',
            initialCode,
            true, // isMain
            false, // isModified
            new MetadataManager(),
            undefined, // decorationManager - will be set in onEditorReady
        );

        // Store initial content in refs for immediate access
        tabContentRef.current.set('main', initialCode);
        tabOriginalContentRef.current.set('main', initialCode);

        // Initialize refs with main tab
        tabsRef.current = [mainTab];

        return [mainTab];
    });

    // === HELPER FUNCTIONS ===

    /**
     * Update tabs ref when React state changes
     * Keeps ref in sync for immediate access without re-renders
     * @param newTabs - Updated tabs array
     */
    const updateTabsRef = (newTabs: Tab[]) => {
        tabsRef.current = newTabs;
    };

    /**
     * Update active tab ID in both state and ref
     * Ensures both React state and ref stay synchronized
     * @param tabId - New active tab ID
     */
    const updateActiveTabId = (tabId: string) => {
        setActiveTabId(tabId); // Triggers re-render
        activeTabIdRef.current = tabId; // Immediate access
    };

    return {
        // === REACT STATE ===
        /** Current tabs array (triggers re-renders) */
        tabs,
        /** React state setter for tabs */
        setTabs,
        /** Current active tab ID (triggers re-renders) */
        activeTabId,
        /** React state setter for active tab */
        setActiveTabId,

        // === REFS FOR IMMEDIATE ACCESS ===
        /** Tab content storage (tabId -> code) */
        tabContentRef,
        /** Original content for modification tracking (tabId -> original code) */
        tabOriginalContentRef,
        /** Current active tab ID (immediate access, no re-render) */
        activeTabIdRef,
        /** All tabs array (immediate access, no re-render) */
        tabsRef,

        // === HELPER METHODS ===
        /** Sync tabs ref with React state */
        updateTabsRef,
        /** Update active tab in both state and ref */
        updateActiveTabId,
    };
};
